import React, { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { AppProvider, useApp } from './contexts/AppContext';
import { Toaster } from 'react-hot-toast';
import LoginPage from './components/Auth/LoginPage';
import SignupPage from './components/Auth/SignupPage';
import AuthCallback from './components/Auth/AuthCallback';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import OverviewPage from './components/HomePage/OverviewPage/OverviewPage.js';
import BudgetPage from './components/HomePage/BudgetPageNew/BudgetPage.js';
// import TopSection from './components/TopSection/TopSection.js';
import HamburgerMenu from './components/SideBar/HamburgerMenu.js';
import Sidebar from './components/SideBar/Sidebar.js';
import SchedulePage from './components/HomePage/SchedulePage/SchedulePage.js';
import BOQPage from './components/BOQ/BOQPage.js';
import SpacesPage from './components/Spaces/SpacesPage.js';
import VendorsPage from './components/Vendors/VendorsPage.js';
import FilesPage from './components/Files/FilesPage.js';
import PublicQuoteView from './components/Vendors/PublicQuoteView.js';
import PublicBOQQuoteView from './components/BOQ/PublicBOQQuoteView.js';
import ModalManager from './components/Modal/ModalManager';
import AuthGuard from './components/Auth/AuthGuard';
import OnboardingPage from './components/Onboarding/OnboardingPage';
import OnboardingWizard from './components/Onboarding/Wizard';
import { TermsOfService, PrivacyPolicy } from './components/Legal';
import { LandingPage } from './components/Landing';
import BBMappingPage from './components/Admin/BBMappingPage';
import BOQDepMappingPage from './components/Admin/BOQDepMappingPage';
import BundleReviewPage from './components/Admin/BundleReviewPage';
import ItemSpaceAssignmentPage from './components/Admin/ItemSpaceAssignmentPage';
import ChoiceBundleAuditPage from './components/Admin/ChoiceBundleAuditPage';
import VendorTypeMappingPage from './components/Admin/VendorTypeMappingPage';
import MasterItemManagerPage from './components/Admin/MasterItemManagerPage';
import CostTypeMappingPage from './components/Admin/CostTypeMappingPage';
import ItemOptionSpaceMappingPage from './components/Admin/ItemOptionSpaceMappingPage';
import { supabase } from './config/supabase';
import './styles/global.css';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function AuthenticatedLayout() {
  const { projects } = useApp();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect to wizard only if projects have loaded and there are none
  if (projects !== null && projects.length === 0) {
    return <Navigate to="/wizard" replace />;
  }

  return (
    <div className="app" dir="rtl">
      {/* <TopSection onMenuClick={toggleSidebar} isSidebarOpen={isSidebarOpen} /> */}
      {!isSidebarOpen && <HamburgerMenu onClick={toggleSidebar} />}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onNavItemClick={toggleSidebar} />
        <div className={`content-area ${isSidebarOpen ? 'sidebar-open' : ''}`}>
          <Routes>
            <Route index element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/budget" element={<BudgetPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/boq/*" element={<BOQPage />} />
            <Route path="/spaces" element={<SpacesPage />} />
            <Route path="/spaces/:spaceId" element={<SpacesPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/project-settings/*" element={<OnboardingPage />} />
            <Route path="/admin/bb-mapping" element={<BBMappingPage />} />
            <Route path="/admin/boq-dep-mapping" element={<BOQDepMappingPage />} />
            <Route path="/admin/bundle-review" element={<BundleReviewPage />} />
            <Route path="/admin/item-spaces" element={<ItemSpaceAssignmentPage />} />
            <Route path="/admin/choice-audit" element={<ChoiceBundleAuditPage />} />
            <Route path="/admin/vendor-type-mapping" element={<VendorTypeMappingPage />} />
            <Route path="/admin/item-manager" element={<MasterItemManagerPage />} />
            <Route path="/admin/cost-type-mapping" element={<CostTypeMappingPage />} />
            <Route path="/admin/item-option-spaces" element={<ItemOptionSpaceMappingPage />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <SessionContextProvider supabaseClient={supabase}>
      <QueryClientProvider client={queryClient}>
        <AppProvider>
          <BrowserRouter>
            <ModalManager />
            <Toaster />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/quote/view/:token" element={<PublicQuoteView />} />
              <Route path="/quote/boq/:token" element={<PublicBOQQuoteView />} />
              <Route
                path="/wizard/*"
                element={
                  <AuthGuard>
                    <OnboardingWizard />
                  </AuthGuard>
                }
              />
              <Route
                path="/*"
                element={
                  <AuthGuard>
                    <AuthenticatedLayout />
                  </AuthGuard>
                }
              />
            </Routes>
          </BrowserRouter>
        </AppProvider>
      </QueryClientProvider>
    </SessionContextProvider>
  );
}

export default App;
